import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Search,
  X,
  Layers,
  TrendingUp,
  AlertTriangle,
  ArrowUpRight,
  BarChart3,
  Activity,
} from "lucide-react";

/**
 * ItemMovementAnalysis
 *
 * Shows items bucketed into Fast Moving / Slow Moving / Non Moving.
 * Matches your API's three separate result sets — each bucket has its own
 * schema, so pass each one in directly (no client-side classification):
 *
 * Fast / Slow moving rows: { ItemCode, ItemDescription, TotalSoldQty, TransactionCount }
 * Non moving rows:         { ItemCode, ItemDescription, CurrentQty, AvgCost, StockValue }
 */

const BUCKETS = [
  {
    key: "FAST",
    label: "Fast Moving",
    color: "#0F6E5F",
    soft: "#E4F2EE",
    icon: TrendingUp,
    gradient: "from-emerald-50 to-teal-50",
  },
  {
    key: "SLOW",
    label: "Slow Moving",
    color: "#B7791F",
    soft: "#FBF0DD",
    icon: Activity,
    gradient: "from-amber-50 to-orange-50",
  },
  {
    key: "NON",
    label: "Non Moving",
    color: "#B42318",
    soft: "#FBE7E5",
    icon: AlertTriangle,
    gradient: "from-rose-50 to-red-50",
  },
];

// Fixed column schemas per bucket, matched to the real API response shapes.
const COLUMN_SETS = {
  FAST: [
    { key: "ItemCode", label: "Item Code", align: "left", mono: true },
    { key: "ItemDescription", label: "Description", align: "left", wrap: true },
    {
      key: "TotalSoldQty",
      label: "Qty Sold",
      align: "right",
      mono: true,
      decimals: 2,
    },
    {
      key: "TransactionCount",
      label: "Transactions",
      align: "right",
      mono: true,
      decimals: 0,
    },
  ],
  SLOW: [
    { key: "ItemCode", label: "Item Code", align: "left", mono: true },
    { key: "ItemDescription", label: "Description", align: "left", wrap: true },
    {
      key: "TotalSoldQty",
      label: "Qty Sold",
      align: "right",
      mono: true,
      decimals: 2,
    },
    {
      key: "TransactionCount",
      label: "Transactions",
      align: "right",
      mono: true,
      decimals: 0,
    },
  ],
  NON: [
    { key: "ItemCode", label: "Item Code", align: "left", mono: true },
    { key: "ItemDescription", label: "Description", align: "left", wrap: true },
    {
      key: "CurrentQty",
      label: "Current Qty",
      align: "right",
      mono: true,
      decimals: 2,
    },
    {
      key: "AvgCost",
      label: "Avg Cost",
      align: "right",
      mono: true,
      decimals: 2,
    },
    {
      key: "StockValue",
      label: "Stock Value",
      align: "right",
      mono: true,
      decimals: 2,
    },
  ],
};

function formatCell(value, col) {
  if (value === null || value === undefined || value === "") return "—";
  if (col.decimals !== undefined) {
    const n = Number(value);
    if (!isNaN(n)) {
      return n.toLocaleString(undefined, {
        minimumFractionDigits: col.decimals,
        maximumFractionDigits: col.decimals,
      });
    }
  }
  return String(value);
}

export default function StockMovement({
  fastMovingItems = [],
  slowMovingItems = [],
  nonMovingItems = [],
  loading = false,
  asOfDate = "",
}) {
  const [activeBucket, setActiveBucket] = useState(null); // null = modal closed
  const [search, setSearch] = useState("");

  const grouped = {
    FAST: fastMovingItems || [],
    SLOW: slowMovingItems || [],
    NON: nonMovingItems || [],
  };

  const activeItems = activeBucket ? grouped[activeBucket] || [] : [];
  const columns = activeBucket ? COLUMN_SETS[activeBucket] : [];

  const filteredItems = useMemo(() => {
    if (!search.trim()) return activeItems;
    const q = search.trim().toLowerCase();
    return activeItems.filter((row) =>
      Object.values(row).some((v) =>
        String(v ?? "")
          .toLowerCase()
          .includes(q)
      )
    );
  }, [activeItems, search]);

  const total = grouped.FAST.length + grouped.SLOW.length + grouped.NON.length;

  const currentBucketConfig = useMemo(() => {
    return BUCKETS.find((b) => b.key === activeBucket);
  }, [activeBucket]);

  // Dynamically calculate summary card totals inside the modal popup
  const bucketTotals = useMemo(() => {
    if (!activeBucket) return {};

    if (activeBucket === "FAST" || activeBucket === "SLOW") {
      const qtySold = filteredItems.reduce(
        (sum, item) => sum + (Number(item.TotalSoldQty) || 0),
        0
      );
      const txCount = filteredItems.reduce(
        (sum, item) => sum + (Number(item.TransactionCount) || 0),
        0
      );
      return { qtySold, txCount };
    } else {
      const currentQty = filteredItems.reduce(
        (sum, item) => sum + (Number(item.CurrentQty) || 0),
        0
      );
      const stockVal = filteredItems.reduce(
        (sum, item) => sum + (Number(item.StockValue) || 0),
        0
      );
      return { currentQty, stockVal };
    }
  }, [activeBucket, filteredItems]);

  const openBucket = (key) => {
    setSearch("");
    setActiveBucket(key);
  };

  const closeModal = () => setActiveBucket(null);

  useEffect(() => {
    if (!activeBucket) return;
    function onKeyDown(e) {
      if (e.key === "Escape") closeModal();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [activeBucket]);

  return (
    <div
      style={{
        "--ink": "#152238",
        "--paper": "#F5F6F8",
        "--surface": "#FFFFFF",
        "--line": "#DCE1E8",
        "--accent": "#0F6E5F",
        "--accent-soft": "#E4F2EE",
        "--muted": "#5B6472",
      }}
      className="w-full text-[var(--ink)] font-sans px-2 pb-4"
    >
      <style>{`
        .mono { font-family: "IBM Plex Mono", "SFMono-Regular", Menlo, Consolas, monospace; font-variant-numeric: tabular-nums; }
        .move-table tbody tr:hover { background: #f8fafc; }
      `}</style>

      <div className="flex items-baseline justify-between mb-3 p-0">
        <p className="text-xs text-gray-500 uppercase tracking-wider">
          Stock Movement Analysis
        </p>
      </div>

      {/* Summary cards — click to open table in a popup */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {BUCKETS.map((b) => {
          const count = grouped[b.key].length;
          return (
            <button
              key={b.key}
              onClick={() => openBucket(b.key)}
              className="text-left rounded-lg border p-3 transition hover:shadow-md"
              style={{
                borderColor: "var(--line)",
                background: "var(--surface)",
              }}
            >
              <div className="flex items-center gap-2 mb-1">
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ background: b.color }}
                />
                <span className="text-xs font-medium text-[var(--muted)]">
                  {b.label}
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span
                  className="text-2xl font-semibold mono"
                  style={{ color: b.color }}
                >
                  {count}
                </span>
              </div>

              <div
                style={{
                  marginTop: 4,
                  fontSize: 10,
                  fontWeight: 500,
                  color: "#94a3b8",
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  whiteSpace: "nowrap",
                }}
              >
                <span>Click to view</span>
                <span style={{ fontSize: 10 }}>→</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Popup modal structured exactly like SalesDetailTable's popup */}
      {activeBucket &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8"
            style={{ background: "rgba(15, 23, 42, 0.45)" }}
            onClick={closeModal}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[70vh] flex flex-col overflow-hidden border border-slate-200"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 border-b border-slate-200">
                <div className="flex items-center justify-between w-full sm:w-auto">
                  <div className="flex items-center gap-2.5">
                    {/* <span
                      className="w-3 h-3 rounded-full shrink-0"
                      style={{ background: currentBucketConfig?.color }}
                    /> */}

                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-widest flex items-center gap-2">
                      <span className="w-1.5 h-5 bg-gradient-to-b from-rose-500 to-pink-500 rounded-full" />
                    </h3>

                    <h3 className="font-extrabold text-slate-800 text-base">
                      {currentBucketConfig?.label} Items ({filteredItems.length}
                      )
                    </h3>
                  </div>
                  <button
                    onClick={closeModal}
                    aria-label="Close"
                    className="sm:hidden text-slate-400 hover:text-slate-600 transition"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="flex items-center gap-3">
                  <div className="relative w-full sm:w-48">
                    <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search items…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-8 border border-slate-200 rounded-lg py-1.5 text-xs bg-slate-50 w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white"
                    />
                  </div>
                  <button
                    onClick={closeModal}
                    aria-label="Close"
                    className="hidden sm:inline-block text-slate-400 hover:text-slate-600 transition"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Modal Body with dynamic structural summary cards */}
              <div className="overflow-y-auto flex-1 p-4 sm:p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3">
                  {/* Summary Card 1: Bucket Description & Context */}
                  <div
                    className={`text-center p-3 sm:p-4 bg-gradient-to-r ${
                      currentBucketConfig?.gradient ||
                      "from-slate-50 to-slate-100"
                    } rounded-xl`}
                  >
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center justify-center gap-1">
                      {React.createElement(
                        currentBucketConfig?.icon || Layers,
                        {
                          size: 11,
                          style: { color: currentBucketConfig?.color },
                        }
                      )}
                      Movement Profile
                    </p>
                    <p className="text-sm font-extrabold text-slate-900 mt-1 truncate">
                      {currentBucketConfig?.label}
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5 flex items-center justify-center gap-1">
                      {filteredItems.length} Records Found
                    </p>
                  </div>

                  {/* Summary Card 2: Primary Metric */}
                  <div className="text-center p-3 sm:p-4 bg-gradient-to-r from-indigo-50 to-violet-50 rounded-xl">
                    {activeBucket === "NON" ? (
                      <>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center justify-center gap-1">
                          <Layers size={11} className="text-indigo-500" />
                          Total Inventory Qty
                        </p>
                        <p className="text-lg sm:text-2xl font-extrabold text-slate-900 mt-1 mono">
                          {bucketTotals.currentQty?.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center justify-center gap-1">
                          <ArrowUpRight size={11} className="text-indigo-500" />
                          Total Qty Sold
                        </p>
                        <p className="text-lg sm:text-2xl font-extrabold text-slate-900 mt-1 mono">
                          {bucketTotals.qtySold?.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })}
                        </p>
                      </>
                    )}
                  </div>

                  {/* Summary Card 3: Secondary Metric */}
                  <div className="text-center p-3 sm:p-4 bg-gradient-to-r from-sky-50 to-cyan-50 rounded-xl">
                    {activeBucket === "NON" ? (
                      <>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center justify-center gap-1">
                          <BarChart3 size={11} className="text-sky-500" />
                          Total Stock Value
                        </p>
                        <p className="text-lg sm:text-2xl font-extrabold text-slate-900 mt-1 mono">
                          {bucketTotals.stockVal?.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center justify-center gap-1">
                          <BarChart3 size={11} className="text-sky-500" />
                          Total Transactions
                        </p>
                        <p className="text-lg sm:text-2xl font-extrabold text-slate-900 mt-1 mono">
                          {bucketTotals.txCount?.toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                          })}
                        </p>
                      </>
                    )}
                  </div>
                </div>

                {/* Table Container */}
                <div className="overflow-hidden rounded-xl border border-slate-200">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs move-table">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          {columns.map((col) => (
                            <th
                              key={col.key}
                              className={`px-3 py-2 text-[9px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap ${
                                col.align === "right"
                                  ? "text-right"
                                  : "text-left"
                              }`}
                            >
                              {col.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {loading && (
                          <tr>
                            <td
                              colSpan={columns.length}
                              className="px-4 py-12 text-center text-slate-400 text-xs"
                            >
                              Loading inventory details...
                            </td>
                          </tr>
                        )}

                        {!loading && filteredItems.length === 0 && (
                          <tr>
                            <td
                              colSpan={columns.length}
                              className="px-4 py-12 text-center text-slate-400 text-xs"
                            >
                              No records found for this movement category.
                            </td>
                          </tr>
                        )}

                        {!loading &&
                          filteredItems.map((row, i) => (
                            <tr
                              key={row.ItemCode ? `${row.ItemCode}-${i}` : i}
                              className="hover:bg-slate-50 transition-colors"
                            >
                              {columns.map((col) => (
                                <td
                                  key={col.key}
                                  className={`px-3 py-2.5 ${
                                    col.align === "right"
                                      ? "text-right"
                                      : "text-left"
                                  } ${col.mono ? "mono" : ""} ${
                                    col.wrap
                                      ? "whitespace-normal break-words max-w-[240px]"
                                      : "whitespace-nowrap"
                                  }`}
                                >
                                  {col.key === "ItemCode" ? (
                                    <span className="text-[10px] font-bold text-slate-700">
                                      {formatCell(row[col.key], col)}
                                    </span>
                                  ) : col.key === "ItemDescription" ? (
                                    <span className="text-[10px] font-semibold text-slate-800">
                                      {formatCell(row[col.key], col)}
                                    </span>
                                  ) : col.align === "right" ? (
                                    <span
                                      className={`text-[10px] font-bold ${
                                        activeBucket === "NON" &&
                                        col.key === "StockValue"
                                          ? "text-indigo-600"
                                          : "text-slate-600"
                                      }`}
                                    >
                                      {formatCell(row[col.key], col)}
                                    </span>
                                  ) : (
                                    <span className="text-[10px] text-slate-600">
                                      {formatCell(row[col.key], col)}
                                    </span>
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                      </tbody>
                      {/* Matching styling for Table Footer totals */}
                      {!loading && filteredItems.length > 0 && (
                        <tfoot>
                          <tr className="bg-slate-50 border-t-2 border-slate-200">
                            <td className="px-3 py-2.5 text-[10px] font-bold text-slate-600 uppercase tracking-wider whitespace-nowrap">
                              Total
                            </td>
                            <td className="px-3 py-2.5" />{" "}
                            {/* Empty spacing cell for description */}
                            {activeBucket === "NON" ? (
                              <>
                                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                                  <span className="text-xs font-extrabold text-slate-700 mono">
                                    {bucketTotals.currentQty?.toLocaleString(
                                      undefined,
                                      { minimumFractionDigits: 2 }
                                    )}
                                  </span>
                                </td>
                                <td className="px-3 py-2.5" />{" "}
                                {/* Empty spacing cell for Average Cost */}
                                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                                  <span className="text-xs font-extrabold text-indigo-600 mono">
                                    {bucketTotals.stockVal?.toLocaleString(
                                      undefined,
                                      { minimumFractionDigits: 2 }
                                    )}
                                  </span>
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                                  <span className="text-xs font-extrabold text-slate-700 mono">
                                    {bucketTotals.qtySold?.toLocaleString(
                                      undefined,
                                      { minimumFractionDigits: 2 }
                                    )}
                                  </span>
                                </td>
                                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                                  <span className="text-xs font-extrabold text-indigo-600 mono">
                                    {bucketTotals.txCount?.toLocaleString(
                                      undefined,
                                      { minimumFractionDigits: 0 }
                                    )}
                                  </span>
                                </td>
                              </>
                            )}
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              </div>

              {/* button */}
              <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
                <button
                  onClick={closeModal}
                  className="w-full py-2 bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all"
                >
                  Close
                </button>
              </div>
              {/* button */}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
